import PageSeo from '@/seo/PageSeo';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/16/solid';
import InteractiveConsole from '@/components/Tools/API/InteractiveConsole';
import ApiConsole from '@/components/API/Console/ApiConsole';
import { isNativeApiConsoleEnabled } from '@/components/API/Console/isNativeApiConsoleEnabled';
import RestrictedAccess from '@/components/Modal/RestrictedAccess';
import { userAppContext } from '@/context/Context';
import { buildToolAccessContext, canUseTool, getToolAccess } from '@/components/Tools/toolAccess';

export default function InteractiveConsolePage() {
  const navigate = useNavigate();
  const native = isNativeApiConsoleEnabled();
  const { user, isAuthenticated } = userAppContext();
  const accessCtx = buildToolAccessContext(isAuthenticated, user?.['cognito:groups']);
  const access = getToolAccess('api', accessCtx);
  const isRestricted = !canUseTool('api', accessCtx);

  const restrictedMessage = (
    <h6>
      {access.reason ?? 'REST API access is not included in your current plan.'}{' '}
      <a href={access.upgradeHref} style={{ color: '#D02591' }}>
        {access.status === 'login' ? 'Sign in' : 'See plans'}
      </a>
      .
    </h6>
  );

  if (native) {
    return (
      <div style={{ padding: '12px 16px 24px' }}>
        <PageSeo
          title="Gene-Disease Association API Console | DISGENET"
          description="Interactive documentation to query DISGENET REST API endpoints for gene-disease and variant-disease associations."
          path="/interactive-console"
        />
        <RestrictedAccess isRestricted={isRestricted} restrictedMessage={restrictedMessage}>
          <ApiConsole />
        </RestrictedAccess>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        overflow: 'hidden',
      }}
    >
      <PageSeo
        title="Gene-Disease Association API Console | DISGENET"
        description="Interactive documentation to query DISGENET REST API endpoints for gene-disease and variant-disease associations."
        path="/interactive-console"
      />

      <header
        style={{
          padding: '12px 24px',
          borderBottom: '1px solid #e0e8f0',
          background: '#fff',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <button
          type="button"
          onClick={() => navigate('/Tools?section=console')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#0d6efd',
            fontSize: 14,
            padding: '4px 0',
          }}
          aria-label="Go back to Tools"
        >
          <ArrowLeftIcon style={{ width: 16, height: 16 }} />
          Back
        </button>

        <div style={{ width: 1, height: 20, background: '#dee2e6' }} />

        <h1
          style={{
            margin: 0,
            fontSize: 18,
            fontWeight: 700,
            color: '#1a2e44',
          }}
        >
          Interactive Console
        </h1>
      </header>

      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>
        <p className="mb-2 text-sm text-gray-600">
          <Link to="/Tools?section=console" className="text-blue-600 underline">
            Step-by-step authorization guide
          </Link>
        </p>
        <InteractiveConsole fullHeight />
      </div>
    </div>
  );
}
